import { useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { ApiError, get, post } from "../api";
import { Avatar, Banner, Pattern, previewEquipped, useSeen, usePortrait, type Equipped } from "../components/Avatar";
import { Figure, type FigureHandle } from "../components/Figure";
import { Coins, ErrorBox, Modal, Progress, Spinner } from "../components/ui";
import { coinShower } from "../components/fx";
import { PageHero } from "../components/PageHero";
import { LuLock, LuShoppingBag, LuTarget } from "react-icons/lu";
import type { Framing } from "../char3d/frame";
import { MYTHIC_FROM, TIER_LABEL, tierOf, type EmoteKind, type Tier } from "../../shared/avatar";
import { GoalCard, HeroFigure, UnlockCeremony, fmt, heroKit, goalPct, type Goal } from "../components/heroes";
import { useApp, useLoad } from "../store";
import { Link } from "../router";

interface Item { id: string; category: string; name: string; description: string; price: number; rarity: string; free: boolean; notForSale: boolean; data: Record<string, any>; owned: boolean; equipped: boolean }

/** the shelves, in the order of the tabs (labels as the children know them) */
const CATS: [string, string][] = [
  ["hero", "Helte"], ["skin", "Figurer"], ["outfit", "Outfits"], ["hat", "Hatte & hjelme"], ["accessory", "Briller & masker"], ["back", "Rygge & vinger"], ["hand", "Udstyr"], ["pet", "Kæledyr"], ["aura", "Auraer"],
  ["emote", "Emotes"], ["background", "Baggrunde"], ["frame", "Rammer"], ["banner", "Bannere"], ["title", "Titler"], ["cardback", "Kortryg"], ["boardtheme", "Brætter"],
  ["car", "Biler"], ["boat", "Både"], ["plane", "Fly"], ["heli", "Helikoptere"], ["property", "Huse"],
];
/** Fri Verden: vehicles and homes are bought here too (and in the dealers' windows in the world, with a 3D showroom) */
const WORLD_ICON: Record<string, string> = { car: "🚗", boat: "⛵", plane: "✈️", heli: "🚁", property: "🏠" };
const isWorldCat = (c: string) => c in WORLD_ICON;
/** a little picture for each vehicle and home (the 3D view in the fitting room shows the real thing) */
const WORLD_ICON_BY_ID: Record<string, string> = {
  bybil: "🚗", pickup: "🛻", sport: "🏎️", suv: "🚙", racer: "🏁", muscle: "🚘", hyper: "🏎️",
  jetski: "🌊", fisk: "🎣", speed: "🚤", sejl: "⛵", yacht: "🛥️",
  cub: "🛩️", bi: "🛩️", jet: "✈️",
  mini: "🚁", rescue: "🚑", tung: "🚛",
  lejl: "🏢", raekke: "🏘️", strandhus: "🏖️", villa: "🏡", tagvilla: "🏙️", fyrhus: "🗼",
};
const iconOf = (it: { id: string; category: string }) => WORLD_ICON_BY_ID[it.id.replace(/^(veh_|home_)/, "")] ?? WORLD_ICON[it.category] ?? "🏠";
const isVehicleCat = (c: string) => c === "car" || c === "boat" || c === "plane" || c === "heli";
const FIGURE_CATS = new Set(["skin", "outfit", "hat", "accessory", "back", "hand", "pet", "aura"]);
const FRAMING: Record<string, Framing> = { skin: "full", outfit: "full", hat: "bust", accessory: "head", back: "backhalf", hand: "full", pet: "full", aura: "full" };
const TIERS: Tier[] = ["common", "rare", "epic", "legendary", "mythic"];
const POSE_AT: Partial<Record<EmoteKind, number>> = { wave: 0.8, gg: 0.8, cheer: 0.8, confetti: 0.8, rocket: 0.9, bolt: 0.8, dance: 0.8, flex: 0.8, spin: 0.5, flip: 0.55, bow: 0.8, think: 0.8, laugh: 0.8, shrug: 0.8, salute: 0.8, point: 0.8, sit: 1.4, float: 1.4 };
const QUICK_EMOTES: [EmoteKind, string][] = [["wave", "Vink"], ["cheer", "Jubel"], ["dance", "Dans"], ["flex", "Muskler"], ["flip", "Flip"], ["confetti", "Konfetti"]];
/** the collector rank: what all the things you own are worth together */
const RANKS: [number, string][] = [[0, "Ny i butikken"], [500, "Samler"], [3000, "Stilikon"], [12000, "Trendsætter"], [40000, "Legende"], [100000, "Mytisk samler"]];

const tierName = (it: Item) => TIER_LABEL[tierOf(it.rarity, it.price)];

/** the figure (skin + outfit) of the player – what a thumbnail is dressed in */
function thumbEquipped(equipped: Equipped, it: Item): Equipped {
  const asItem = { id: it.id, data: it.data, name: it.name };
  if (it.category === "hero") return previewEquipped({}, it);
  if (it.category === "skin") return { skin: asItem };
  const base: Equipped = { skin: equipped?.skin, outfit: equipped?.outfit };
  if (it.category === "outfit") return { skin: equipped?.skin, outfit: asItem };
  return { ...base, [it.category]: asItem };
}

function Thumb({ it, equipped }: { it: Item; equipped: Equipped }) {
  const [ref, seen] = useSeen<HTMLDivElement>();
  const isFig = FIGURE_CATS.has(it.category) || it.category === "emote";
  const pose = it.category === "emote" ? { kind: it.data.kind as EmoteKind, at: POSE_AT[it.data.kind as EmoteKind] ?? 0.8 } : undefined;
  const eq = useMemo(() => (it.category === "emote" ? { skin: equipped?.skin, outfit: equipped?.outfit } : thumbEquipped(equipped, it)), [equipped, it]);
  const framing: Framing = it.category === "emote" ? "full" : FRAMING[it.category] ?? "full";
  const shot = usePortrait(isFig ? eq : null, framing, 150, { enabled: seen && isFig, pose });
  const tone = it.category === "background" || it.category === "frame" || it.category === "banner";
  return (
    <div ref={ref} className="thumb">
      {isFig && (shot ? <img src={shot} alt="" draggable={false} /> : <div className="skel" aria-hidden="true" />)}
      {(it.category === "background" || it.category === "frame") && <Avatar equipped={previewEquipped(equipped, it)} size={118} label="" />}
      {it.category === "banner" && <div style={{ width: "100%" }}><Banner item={it} height={86} /></div>}
      {it.category === "title" && <div className="ttl"><span>{it.data.text}</span></div>}
      {it.category === "cardback" && <div className="cbk" style={{ background: `linear-gradient(145deg, ${it.data.from}, ${it.data.to})` }}><span>{it.data.symbol}</span></div>}
      {it.category === "boardtheme" && (
        <div className="mboard" aria-hidden="true">
          {Array.from({ length: 16 }, (_, i) => <i key={i} style={{ background: (i + Math.floor(i / 4)) % 2 ? it.data.dark : it.data.light }} />)}
          <b style={{ background: it.data.accent }} />
        </div>
      )}
      {isWorldCat(it.category) && <div className={`vthumb ${it.category}`} aria-hidden="true"><span>{iconOf(it)}</span></div>}
      {!tone && !isFig && null}
    </div>
  );
}

/** one hero in the gallery: a big picture in full colour, name, short motto, price and state (a small lock shows how far the savings have come) */
function HeroCard({ it, balance, selected, isGoal, onPick }: { it: Item; balance: number; selected: boolean; isGoal: boolean; onPick: () => void }) {
  const [ref, seen] = useSeen<HTMLDivElement>();
  const t = tierOf(it.rarity, it.price);
  const canBuy = !it.owned && balance >= it.price;
  const [t1, t2] = (it.data.tint as [string, string] | undefined) ?? ["#4cc9f0", "#6366f1"];
  return (
    <div
      ref={ref}
      className={`herocard shopcard tier-${t} ${selected ? "sel" : ""} ${it.equipped ? "worn" : ""} ${isGoal ? "isgoal" : ""} ${it.owned ? "owned" : ""}`}
      style={{ ["--t1" as string]: t1, ["--t2" as string]: t2 }}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${it.name}, ${TIER_LABEL[t]}${it.owned ? ", ejet" : `, ${it.price} mønter`}${isGoal ? ", dit sparemål" : ""}`}
      data-tilt="6"
      onClick={onPick}
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(); } }}
    >
      <div className="hc-fig"><HeroFigure hero={it} size={176} enabled={seen} /></div>
      {isGoal && <span className="hc-flag"><LuTarget aria-hidden="true" size={14} /> Sparemål</span>}
      {!it.owned && <span className={`hc-lock${canBuy ? " ready" : ""}`} aria-hidden="true">{canBuy ? "Klar til køb" : <><LuLock size={13} /> {goalPct(balance, it.price)}%</>}</span>}
      <div className="hc-meta"><strong>{it.name}</strong><em>{String(it.data.epithet ?? "")}</em></div>
      <div className="sc-foot">
        <span className={`tierchip tier-${t}`}>{TIER_LABEL[t]}</span>
        {it.equipped ? <span className="state on">På ✓</span> : it.owned ? <span className="state own">Ejet</span> : <span className={`price ${balance >= it.price ? "ok" : ""}`}><Coins n={it.price} /></span>}
      </div>
      {!it.owned && <div className="hc-meter" aria-hidden="true"><i style={{ width: `${goalPct(balance, it.price)}%` }} /></div>}
    </div>
  );
}

/** a bought-or-not vehicle in the fitting room: the same turning 3D showroom as at the dealers in Fri Verden (fetched only when a vehicle is chosen) */
function VehicleStage({ id }: { id: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const room = useRef<{ show(k: any, c: string): void; dispose(): void } | null>(null);
  const idRef = useRef(id);
  idRef.current = id;
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let dead = false;
    void import("../world/preview3d").then(({ Showroom }) => {
      if (dead || !ref.current) return;
      try { const r = new Showroom(ref.current, false); room.current = r; r.show(idRef.current as any, "#ef4444"); } catch { setFailed(true); }
    });
    return () => { dead = true; room.current?.dispose(); room.current = null; };
  }, []);
  useEffect(() => { room.current?.show(id, "#ef4444"); }, [id]);
  return (
    <div className="shop-vstage">
      <canvas ref={ref} aria-label="3D-visning – træk for at dreje" />
      {failed && <p className="muted" style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", margin: 0 }}>Din enhed kan ikke vise 3D her.</p>}
    </div>
  );
}

function Stage({ equipped, preview, figureRef }: { equipped: Equipped; preview: Item | null; figureRef: { current: FigureHandle | null } }) {
  const shown = preview ? previewEquipped(equipped, preview) : equipped;
  const bg = (shown?.background?.data as Record<string, any> | undefined) ?? { from: "#0b1030", to: "#312e81", pattern: "grid" };
  const tier = preview ? tierOf(preview.rarity, preview.price) : "common";
  return (
    <div className={`shop-stage tier-${tier}`} style={{ background: `linear-gradient(160deg, ${bg.from}, ${bg.to})` }}>
      <svg className="pat" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" aria-hidden="true"><Pattern kind={bg.pattern} id="stage" /></svg>
      <div className="beam" aria-hidden="true" />
      <div className="floorglow" aria-hidden="true" />
      <div className="turntable" aria-hidden="true" />
      <Figure ref={figureRef as any} equipped={shown} framing="hero" label="Dit prøverum – træk for at dreje figuren, klik for at få den til at reagere" />
    </div>
  );
}

export function Shop() {
  const { me, reload: reloadMe, toast } = useApp();
  const shop = useLoad(() => get<{ balance: number; items: Item[]; goal: Goal | null }>("/api/shop"), []);
  const qs = useMemo(() => new URLSearchParams(typeof location !== "undefined" ? location.search : ""), []);
  const [cat, setCat] = useState(CATS.some(([id]) => id === qs.get("fane")) ? qs.get("fane")! : "hero");
  const [mode, setMode] = useState<"shop" | "inv">("shop");
  const [tier, setTier] = useState<Tier | "all">("all");
  const [sort, setSort] = useState<"asc" | "desc" | "name">("asc");
  const [selId, setSelId] = useState<string | null>(qs.get("valg"));
  const [confirm, setConfirm] = useState<Item | null>(null);
  const [ceremony, setCeremony] = useState<Item | null>(null);
  const [busy, setBusy] = useState(false);
  const fig = useRef<FigureHandle | null>(null);
  const equipped = useMemo(() => (me?.avatar ?? {}) as Equipped, [me]);

  const items = shop.data?.items ?? [];
  const balance = shop.data?.balance ?? 0;
  const sel = useMemo(() => items.find((i) => i.id === selId) ?? null, [items, selId]);
  /** the item the player is saving up for (kept by the server, shown on the front page and after every game) */
  const saving = me?.goal ?? null;

  /* arriving from a link ("Hent Drage-Dorte" on the front page): open the shelf the item lives on, once */
  const jumped = useRef(false);
  useEffect(() => {
    if (jumped.current || !items.length) return;
    jumped.current = true;
    const want = qs.get("valg");
    const it = want ? items.find((i) => i.id === want) : null;
    if (it) setCat(it.category);
  }, [items, qs]);

  /* trying an emote on: let the figure play it */
  useEffect(() => {
    if (sel?.category === "emote") { const t = setTimeout(() => fig.current?.play(sel.data.kind as EmoteKind), 350); return () => clearTimeout(t); }
  }, [sel?.id, sel?.category]);

  const sellable = useMemo(() => items.filter((i) => !i.notForSale || i.owned), [items]);
  const perCat = useMemo(() => {
    const m: Record<string, { own: number; all: number }> = {};
    for (const i of sellable) { const r = (m[i.category] ??= { own: 0, all: 0 }); r.all++; if (i.owned) r.own++; }
    return m;
  }, [sellable]);
  const worth = useMemo(() => items.filter((i) => i.owned).reduce((s, i) => s + i.price, 0), [items]);
  const rankIdx = RANKS.reduce((k, [from], i) => (worth >= from ? i : k), 0);
  const nextRank = RANKS[rankIdx + 1];

  const list = useMemo(() => {
    let l = sellable.filter((i) => i.category === cat && (mode === "inv" ? i.owned : true));
    if (tier !== "all") l = l.filter((i) => tierOf(i.rarity, i.price) === tier);
    const by = { asc: (a: Item, b: Item) => a.price - b.price || a.name.localeCompare(b.name, "da"), desc: (a: Item, b: Item) => b.price - a.price || a.name.localeCompare(b.name, "da"), name: (a: Item, b: Item) => a.name.localeCompare(b.name, "da") }[sort];
    return [...l].sort(by);
  }, [sellable, cat, mode, tier, sort]);
  /** the next thing worth saving for in this shelf */
  const shelfGoal = useMemo(() => sellable.filter((i) => i.category === cat && !i.owned && !i.notForSale && i.price > balance).sort((a, b) => a.price - b.price)[0] ?? null, [sellable, cat, balance]);

  if (shop.loading && !shop.data) return <Spinner />;
  if (shop.error) return <ErrorBox error={shop.error} retry={shop.reload} />;

  const run = async (fn: () => Promise<unknown>, ok: string): Promise<boolean> => {
    setBusy(true);
    let good = false;
    try { await fn(); toast(ok, "ok"); good = true; } catch (e) { toast(e instanceof ApiError ? e.message : "Noget gik galt.", "error"); }
    setBusy(false);
    await Promise.all([shop.reload(), reloadMe()]);
    return good;
  };
  const buy = async (it: Item) => {
    const good = await run(() => post("/api/shop/buy", { id: it.id }), `Du købte ${it.name}!`);
    setConfirm(null);
    if (good && it.category === "hero") {
      setCeremony(it); // the big "new hero" moment has its own coins, confetti and 3D podium
    } else if (good) {
      coinShower(document.querySelector<HTMLElement>(".shop-stage"), it.price > 600 ? 34 : 18);
      const t = tierOf(it.rarity, it.price);
      fig.current?.burst(t === "mythic" ? "stars" : t === "legendary" ? "rocket" : "confetti");
      fig.current?.play(t === "common" || t === "rare" ? "cheer" : "dance");
    }
  };
  const toggleGoal = async (it: Item) => {
    setBusy(true);
    try {
      const clear = saving?.id === it.id;
      await post("/api/shop/goal", { id: clear ? null : it.id });
      toast(clear ? "Sparemålet er fjernet" : `Sparemål: ${it.name} – nu sparer du op!`, "ok");
      await reloadMe();
    } catch (e) { toast(e instanceof ApiError ? e.message : "Noget gik galt.", "error"); }
    setBusy(false);
  };
  const clearGoal = async () => {
    try { await post("/api/shop/goal", { id: null }); toast("Sparemålet er fjernet", "ok"); await reloadMe(); } catch (e) { toast(e instanceof ApiError ? e.message : "Noget gik galt.", "error"); }
  };
  const equip = (it: Item) => run(() => post("/api/inventory/equip", { id: it.id }), `${it.name} er på`).then((ok) => { if (ok) fig.current?.play("wave"); });
  const unequip = (it: Item) => run(() => post("/api/inventory/unequip", { category: it.category }), "Taget af");

  const cta = (it: Item) => {
    if (it.owned && isWorldCat(it.category)) {
      if (it.equipped) return <span className="okmsg">{it.category === "property" ? "Her bor du ✓" : "Du bruger den ✓"}</span>;
      return <button className="btn" disabled={busy} onClick={() => equip(it)}>{it.category === "property" ? "Bo her" : "Brug den"}</button>;
    }
    if (it.owned) {
      if (it.equipped) return it.category === "skin" ? <span className="okmsg">Du har den på ✓</span> : <button className="btn ghost" disabled={busy} onClick={() => unequip(it)}>Tag af</button>;
      return <button className="btn" disabled={busy} onClick={() => equip(it)}>Tag på</button>;
    }
    if (it.notForSale) return <span className="muted">Fås som præmie</span>;
    return (
      <>
        <button className="btn lime" disabled={busy || balance < it.price} onClick={() => setConfirm(it)}>{it.price === 0 ? "Gratis – hent" : <>Køb · <Coins n={it.price} /></>}</button>
        {it.price > 0 && balance < it.price && (
          <button className={`btn ${saving?.id === it.id ? "gold" : "ghost"}`} disabled={busy} aria-pressed={saving?.id === it.id} onClick={() => toggleGoal(it)}>
            <LuTarget aria-hidden="true" size={16} /> {saving?.id === it.id ? "Dit sparemål ✓" : "Spar op til den"}
          </button>
        )}
      </>
    );
  };

  return (
    <div className="stack shop3 rise">
      <PageHero
        tone="shop"
        title="Shop"
        icon={<LuShoppingBag size={44} />}
        sub="Pynt til din figur, biler, både, fly, helikoptere og huse – uden kasser og uden fordele i spil."
      >
        <div className="pill big" role="status" aria-label={`Du har ${balance} mønter`}><Coins n={balance} /></div>
      </PageHero>

      <div className="shop-layout">
        <aside className="shop-side">
          {saving && <GoalCard goal={saving} balance={balance} onClear={clearGoal} className="in-shop" />}
          <div className="card shop-room">
            <div className="row between"><h2 style={{ margin: 0 }}>Prøverum</h2><div className="row" style={{ gap: 6 }}>{sel && <button className="btn ghost small" onClick={() => setSelId(null)}>Stop prøve</button>}</div></div>
            {sel && isVehicleCat(sel.category) ? (
              <VehicleStage id={String(sel.data.vehicle ?? sel.id.replace(/^veh_/, ""))} />
            ) : (
              <>
                <Stage equipped={equipped} preview={sel} figureRef={fig} />
                <div className="chips" role="group" aria-label="Få figuren til at reagere">
                  {QUICK_EMOTES.map(([k, l]) => <button key={k} className="sfilter" onClick={() => fig.current?.play(k)}>{l}</button>)}
                  <button className="sfilter" onClick={() => fig.current?.react()}>Kildr den!</button>
                </div>
              </>
            )}
          </div>

          <div className="card shop-detail" aria-live="polite">
            {sel ? (
              <>
                <div className="row" style={{ alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                    <span className={`tierchip tier-${tierOf(sel.rarity, sel.price)}`}>{tierName(sel)}</span>
                    <h3 style={{ margin: "4px 0 2px" }}>{sel.name}</h3>
                    {sel.category === "hero" ? (
                      <>
                        <p className="muted" style={{ margin: 0 }}><em>{String(sel.data.epithet ?? "")}</em></p>
                        <p style={{ margin: "6px 0 0", fontSize: ".92rem" }}>{String(sel.data.lore ?? "")}</p>
                        {heroKit(sel).length > 0 && <ul className="ul-kit small" aria-label="Det følger med">{heroKit(sel).map((k) => <li key={k}>{k}</li>)}</ul>}
                      </>
                    ) : <p className="muted" style={{ margin: 0 }}>{sel.description}</p>}
                    {sel.category === "skin" && <p className="muted" style={{ margin: "4px 0 0", fontSize: ".85rem" }}>Ny figur = nyt tøj følger med.</p>}
                  </div>
                  {sel.category !== "hero" && !isWorldCat(sel.category) && <Avatar equipped={previewEquipped(equipped, sel)} size={84} label={`${sel.name} på din profil`} />}
                  {isWorldCat(sel.category) && <span className="vbig" aria-hidden="true">{iconOf(sel)}</span>}
                </div>
                {sel.category === "title" && <p style={{ margin: "8px 0 0" }}>Titel på din profil: <strong>{sel.data.text}</strong></p>}
                {isWorldCat(sel.category) && (
                  <>
                    {sel.category === "property" && Array.isArray(sel.data.perks) && <ul className="ul-kit small" aria-label="Det følger med">{(sel.data.perks as string[]).map((k) => <li key={k}>{k}</li>)}</ul>}
                    <p className="muted" style={{ margin: "8px 0 0", fontSize: ".88rem" }}>Bruges i <Link to="/verden">Fri Verden</Link>.</p>
                  </>
                )}
                <div className="row" style={{ marginTop: 10 }}>{cta(sel)}{!sel.owned && !sel.notForSale && balance < sel.price && <small className="muted">Mangler {fmt(sel.price - balance)} mønter</small>}</div>
              </>
            ) : (
              <p className="muted" style={{ margin: 0 }}>Tryk på en ting – og drej figuren!</p>
            )}
          </div>

          <div className="card shop-prestige">
            <div className="row between"><h3 style={{ margin: 0 }}>Din samling</h3><span className="tierchip tier-legendary">{RANKS[rankIdx]![1]}</span></div>
            <p className="muted" style={{ margin: "4px 0 8px" }}>Samling: <Coins n={worth} />. {nextRank ? <>Næste rang <strong>{nextRank[1]}</strong> ved {fmt(nextRank[0])}.</> : "Højeste rang!"}</p>
            {nextRank && <Progress value={Math.min(worth - RANKS[rankIdx]![0], nextRank[0] - RANKS[rankIdx]![0])} max={nextRank[0] - RANKS[rankIdx]![0]} label={`${fmt(worth)} af ${fmt(nextRank[0])} mønter i samlingen`} />}
          </div>
        </aside>

        <section className="shop-main" aria-label="Varer">
          <div className="row between" style={{ flexWrap: "wrap", gap: 8 }}>
            <div className="tabs" role="tablist" aria-label="Butik eller mine ting">
              <button role="tab" aria-selected={mode === "shop"} className={mode === "shop" ? "on" : ""} onClick={() => setMode("shop")}>Butik</button>
              <button role="tab" aria-selected={mode === "inv"} className={mode === "inv" ? "on" : ""} onClick={() => setMode("inv")}>Mine ting</button>
            </div>
            <label className="row" style={{ gap: 6 }}>
              <span className="muted">Sortér</span>
              <select value={sort} onChange={(e: ChangeEvent<HTMLSelectElement>) => setSort(e.target.value as typeof sort)} aria-label="Sortér varer">
                <option value="asc">Billigst først</option>
                <option value="desc">Dyrest først</option>
                <option value="name">Navn</option>
              </select>
            </label>
          </div>
          <div className="tabs scroll shop-cats" role="tablist" aria-label="Kategorier">
            {CATS.map(([id, name]) => (
              <button key={id} role="tab" aria-selected={cat === id} className={cat === id ? "on" : ""} onClick={() => { setCat(id); setTier("all"); }}>
                {name}<small>{perCat[id]?.own ?? 0}/{perCat[id]?.all ?? 0}</small>
              </button>
            ))}
          </div>
          <div className="chips" role="group" aria-label="Sjældenhed">
            <button className={`sfilter ${tier === "all" ? "on" : ""}`} onClick={() => setTier("all")}>Alle</button>
            {TIERS.map((t) => <button key={t} className={`sfilter tier-${t} ${tier === t ? "on" : ""}`} onClick={() => setTier(t)}>{TIER_LABEL[t]}{t === "mythic" ? ` (fra ${fmt(MYTHIC_FROM)})` : ""}</button>)}
          </div>
          {mode === "shop" && cat === "hero" && !saving && (
            <div className="goal herohint" role="note">
              <span><LuTarget aria-hidden="true" size={18} /> <strong>Spar op til en helt:</strong> tryk på en helt og vælg «Spar op til den». Så følger du med i, hvor langt du er nået, på forsiden og efter hvert spil.</span>
            </div>
          )}
          {mode === "shop" && cat !== "hero" && shelfGoal && (
            <div className="goal" role="note">
              <span>Næste mål i {CATS.find(([id]) => id === cat)![1].toLowerCase()}: <strong>{shelfGoal.name}</strong> ({tierName(shelfGoal)})</span>
              <Progress value={Math.min(balance, shelfGoal.price)} max={shelfGoal.price} label={`${fmt(balance)} af ${fmt(shelfGoal.price)} mønter`} />
            </div>
          )}
          {list.length === 0 && <p className="muted">{mode === "inv" ? "Du ejer ikke noget i den kategori endnu." : "Ingen varer med de valg."}</p>}
          {cat === "hero" && (
            <div className="hero-grid">
              {list.map((it) => <HeroCard key={it.id} it={it} balance={balance} selected={selId === it.id} isGoal={saving?.id === it.id} onPick={() => setSelId(it.id)} />)}
            </div>
          )}
          <div className="shop-grid3" hidden={cat === "hero"}>
            {cat !== "hero" && list.map((it) => {
              const t = tierOf(it.rarity, it.price);
              return (
                <div
                  key={it.id}
                  className={`shopcard tier-${t} ${selId === it.id ? "sel" : ""} ${it.equipped ? "worn" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selId === it.id}
                  aria-label={`${it.name}, ${TIER_LABEL[t]}${it.owned ? ", ejet" : it.notForSale ? ", præmie" : `, ${it.price} mønter`}`}
                  onClick={() => setSelId(it.id)}
                  onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelId(it.id); } }}
                >
                  <Thumb it={it} equipped={equipped} />
                  <div className="sc-meta">
                    <strong>{it.name}</strong>
                    <span className={`tierchip tier-${t}`}>{TIER_LABEL[t]}</span>
                  </div>
                  <div className="sc-foot">
                    {it.equipped ? <span className="state on">På ✓</span> : it.owned ? <span className="state own">Ejet</span> : it.notForSale ? <span className="state">Præmie</span> : it.price === 0 ? <span className="state own">Gratis</span> : <span className={`price ${balance >= it.price ? "ok" : ""}`}><Coins n={it.price} /></span>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {ceremony && (
        <UnlockCeremony
          hero={ceremony}
          worn={!!items.find((i) => i.id === ceremony.id)?.equipped}
          busy={busy}
          onWear={async () => { await equip(ceremony); }}
          onClose={() => setCeremony(null)}
        />
      )}

      {confirm && (
        <Modal title={`Køb ${confirm.name}?`} onClose={() => setConfirm(null)}>
          <p>Pris: <Coins n={confirm.price} /> · bagefter har du <Coins n={balance - confirm.price} />.</p>
          <div className="row">
            <button className="btn lime" disabled={busy} onClick={() => buy(confirm)}>Ja, køb</button>
            <button className="btn ghost" onClick={() => setConfirm(null)}>Annuller</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
